import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Loader2, TrendingUp, Activity } from 'lucide-react';
import { getSymptomsSummary } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import { PeriodPicker } from './PeriodPicker';

/**
 * Trend section for Health page — symptom frequency + category breakdown
 * over a chosen period. Independent of the day-focused logging above.
 */
export function HealthTrendSection() {
    const { language } = useLanguage();
    const [days, setDays] = useState(7);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getSymptomsSummary(days)
            .then(res => { if (!cancelled) setData(res.data); })
            .catch(err => { if (!cancelled) console.error('symptoms summary failed:', err); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [days]);

    // Category translations
    const catLabel = (cat) => {
        const labels = language === 'de'
            ? { mental: 'Mental', physical: 'Körperlich', digestive: 'Verdauung', skin: 'Haut', sleep: 'Schlaf', pain: 'Schmerz', general: 'Allgemein' }
            : { mental: 'Mental', physical: 'Physical', digestive: 'Digestive', skin: 'Skin', sleep: 'Sleep', pain: 'Pain', general: 'General' };
        return labels[cat] || cat;
    };

    const byCategory = data?.by_category || {};
    const sortedCats = Object.entries(byCategory).sort((a, b) => b[1].count - a[1].count);
    const totalEntries = data?.total_entries || 0;
    const topCount = sortedCats[0]?.[1]?.count || 0;

    return (
        <Card data-testid="health-trend-section">
            <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    {language === 'de' ? 'Trend' : 'Trend'}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                <PeriodPicker value={days} onChange={setDays} testid="health-period" />

                {loading ? (
                    <div className="py-6 flex items-center justify-center text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin" />
                    </div>
                ) : totalEntries === 0 ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                        <Activity className="w-8 h-8 mx-auto mb-2 opacity-40" />
                        {language === 'de'
                            ? 'Keine Einträge im Zeitraum'
                            : 'No entries in this period'}
                    </div>
                ) : (
                    <>
                        <div className="flex items-baseline gap-2">
                            <span className="text-2xl font-bold" data-testid="health-trend-total">{totalEntries}</span>
                            <span className="text-xs text-muted-foreground">
                                {language === 'de' ? 'Einträge insgesamt' : 'entries total'}
                            </span>
                        </div>

                        <div className="space-y-1.5">
                            {sortedCats.map(([cat, info]) => {
                                const widthPct = topCount > 0 ? (info.count / topCount) * 100 : 0;
                                return (
                                    <div key={cat} data-testid={`health-trend-cat-${cat}`}>
                                        <div className="flex items-center justify-between text-xs mb-1">
                                            <span className="font-medium">{catLabel(cat)}</span>
                                            <span className="text-muted-foreground">
                                                {info.count} · Ø {info.avg_severity}/10
                                            </span>
                                        </div>
                                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-primary rounded-full transition-all"
                                                style={{ width: `${widthPct}%` }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
}
